"use client";

import { motion, type Variants } from "motion/react";
import { type PointerEvent, useRef } from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import { cn } from "@/lib/utils";

import { useReveal } from "../../../_lib/use-reveal";

import styles from "./glance.module.css";

// =============================================================================

const tileVariants: Variants = {
  hidden: { opacity: 0, y: 48, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.95, ease: EASE_OUT_EXPO },
  },
};

/**
 * Grid of the "At a glance" tiles: they rise one after another once the grid
 * is in view, and a coral spotlight follows the mouse across their borders.
 */
export function GlanceGrid({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const { ref, isRevealed } = useReveal<HTMLDivElement>("0px 0px -12% 0px");
  const frame = useRef(0);

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") return;
    const { clientX, clientY, currentTarget } = event;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      for (const tile of currentTarget.querySelectorAll<HTMLElement>(
        "[data-glance-tile]"
      )) {
        const rect = tile.getBoundingClientRect();
        tile.style.setProperty("--spot-x", `${clientX - rect.left}px`);
        tile.style.setProperty("--spot-y", `${clientY - rect.top}px`);
      }
    });
  };

  const toggleSpotlight = (
    event: PointerEvent<HTMLDivElement>,
    on: boolean
  ) => {
    if (event.pointerType !== "mouse") return;
    event.currentTarget.toggleAttribute("data-spotlight", on);
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isRevealed ? "visible" : "hidden"}
      transition={{ staggerChildren: 0.08 }}
      onPointerMove={onPointerMove}
      onPointerEnter={(event) => toggleSpotlight(event, true)}
      onPointerLeave={(event) => toggleSpotlight(event, false)}
      className={cn(styles.grid, className)}
    >
      {children}
    </motion.div>
  );
}

// =============================================================================

/** Card of the grid: rises with the others, lit by the spotlight */
export function GlanceTile({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      data-glance-tile
      variants={tileVariants}
      className={cn(
        styles.tile,
        "rounded-2xl border border-line bg-surface-1 dark:bg-linear-to-b dark:from-surface-2 dark:to-surface-1",
        className
      )}
    >
      {children}
    </motion.div>
  );
}

// =============================================================================
